import * as THREE from 'three';
import { buildLightmapAtlas } from './uvAtlas.jsx';
import { buildBvh, makeDataTexture, DATA_TEX_WIDTH } from './bvh.jsx';
import {
  MAX_LIGHTS,
  GBUFFER_VERT,
  GBUFFER_FRAG,
  FULLSCREEN_VERT,
  TRACE_FRAG,
  COPY_FRAG,
  DENOISE_FRAG,
  RESOLVE_FRAG,
} from './shaders.jsx';

// «پختنِ نور» روی کارت گرافیکِ خودِ دستگاهِ کاربر (همان کاری که Blender روی سرور می‌کرد).
//
// روند:
//   prepare()  یک بار: UVِ دوم (نقشه‌ی نور)، درختِ مثلث‌ها، جدولِ جنس‌ها، چراغ‌ها، نقشه‌ی موقعیت/جهت
//   step(dt)   هر فریم: یک نوار از نقشه‌ی نور یک نمونه‌ی تازه می‌گیرد. اندازه‌ی نوار با سرعتِ دستگاه تنظیم
//              می‌شود تا هیچ فریمی کارت گرافیک را طولانی قفل نکند (وگرنه سیستم‌عامل صفحه را ری‌ست می‌کند)
//   finish()   حذفِ نویز و آماده‌سازیِ نهایی؛ حافظه‌ی موقت آزاد می‌شود
//   lightMap   تصویرِ نهایی (در طولِ کار هم مرتب به‌روز می‌شود — کاربر واقعی‌شدنِ تدریجی را می‌بیند)
//
// واحدها: متر (گروهِ ساختمان مقیاسِ ۰٫۰۱ دارد). مقدارِ نقشه‌ی نور = تابش؛ روی MeshBasicMaterial با
// lightMapIntensity = ۱ رنگِ درست می‌دهد.

const EMIT_STRENGTH = 4; // نوارهای LED (جنسِ بی‌نور) به‌عنوانِ منبعِ نور
const MIN_BAKE_SIZE_M = 0.12; // اشیای کوچک‌تر (دستگیره و ...) پخته نمی‌شوند؛ با نورِ عادیِ صحنه دیده می‌شوند
const ROOM_LIGHT_L = 0.8; // درخشندگیِ چراغِ سقفی در روشناییِ ۷۰٪
const ROOM_LIGHT_COLOR = new THREE.Color(1.0, 0.93, 0.84);
const ROOM_LIGHT_SHRINK = 0.6; // چراغ = ۶۰٪ِ وسطِ اتاق

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

const avgCache = new Map();
function averageTextureColor(tex) {
  const src = tex?.source?.data || tex?.image;
  if (!src) return null;
  const key = tex.source?.uuid || src;
  if (avgCache.has(key)) return avgCache.get(key);
  let out = null;
  try {
    const S = 48;
    const canvas = document.createElement('canvas');
    canvas.width = S;
    canvas.height = S;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(src, 0, 0, S, S);
    const data = ctx.getImageData(0, 0, S, S).data;
    let r = 0;
    let g = 0;
    let b = 0;
    for (let i = 0; i < data.length; i += 4) {
      r += srgbToLinear(data[i] / 255);
      g += srgbToLinear(data[i + 1] / 255);
      b += srgbToLinear(data[i + 2] / 255);
    }
    const n = data.length / 4;
    out = new THREE.Color(r / n, g / n, b / n);
  } catch (e) {
    out = null;
  }
  avgCache.set(key, out);
  return out;
}

function isShown(obj) {
  for (let o = obj; o; o = o.parent) if (!o.visible) return false;
  return true;
}

function materialKind(mat) {
  if (!mat || Array.isArray(mat)) return 'skip';
  if (mat.visible === false) return 'skip';
  if (mat.transparent || mat.opacity < 1 || (mat.transmission || 0) > 0) return 'skip'; // شیشه — نور از آن رد می‌شود
  if (mat.isMeshBasicMaterial) return 'emitter';
  return 'solid';
}

function makeRT(w, h, type) {
  const rt = new THREE.WebGLRenderTarget(w, h, {
    type,
    format: THREE.RGBAFormat,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
  });
  return rt;
}

export class LightBaker {
  constructor(renderer, root, options = {}) {
    this.renderer = renderer;
    this.root = root;
    this.size = options.size || 2048;
    this.bounces = options.bounces || 4;
    this.targetSamples = options.samples || 512;
    this.clamp = options.clamp || 30;
    this.sun = {
      dir: (options.sunDir || new THREE.Vector3(0.35, 1.15, 0.85)).clone().normalize(),
      E: options.sunE ?? 4.0,
      color: new THREE.Color(1.0, 0.95, 0.86),
    };
    this.samples = 0; // تعدادِ دورِ کامل
    this.frame = 0;
    this.cursor = 0; // ردیفِ بعدی در دورِ جاری
    this.rows = 0; // ردیف در هر تکه (خودکار تنظیم می‌شود)
    this.finished = false;
    this.bakedMeshes = [];
    this.stats = {};
  }

  // -------------------------------------------------------------------------------------------------
  prepare() {
    const { renderer, root, size } = this;
    root.updateMatrixWorld(true);

    const floatOk = renderer.extensions.has('EXT_color_buffer_float');
    this.accType = floatOk ? THREE.FloatType : THREE.HalfFloatType;

    // ۱) دسته‌بندیِ مش‌ها و جدولِ جنس‌ها
    const solids = [];
    const emitters = [];
    const matIndex = new Map();
    const matData = [];
    const box = new THREE.Box3();
    const sizeV = new THREE.Vector3();
    const addMaterial = (mat, kind) => {
      if (matIndex.has(mat)) return matIndex.get(mat);
      let albedo;
      let emission = new THREE.Color(0, 0, 0);
      if (kind === 'emitter') {
        albedo = new THREE.Color(0.8, 0.8, 0.8);
        emission = (mat.color || new THREE.Color(1, 1, 1)).clone().multiplyScalar(EMIT_STRENGTH);
      } else {
        albedo = (mat.color || new THREE.Color(1, 1, 1)).clone();
        const avg = mat.map ? averageTextureColor(mat.map) : null;
        if (avg) albedo.multiply(avg);
        const metal = Math.min(1, Math.max(0, mat.metalness || 0));
        albedo.multiplyScalar(1 - 0.6 * metal);
        if (mat.emissive) emission = mat.emissive.clone().multiplyScalar(mat.emissiveIntensity ?? 1);
      }
      albedo.r = Math.min(albedo.r, 0.92);
      albedo.g = Math.min(albedo.g, 0.92);
      albedo.b = Math.min(albedo.b, 0.92);
      const id = matData.length / 8;
      matData.push(albedo.r, albedo.g, albedo.b, 0, emission.r, emission.g, emission.b, 0);
      matIndex.set(mat, id);
      return id;
    };

    const floors = [];
    const ceilingMeshes = [];
    root.traverse((obj) => {
      if (obj.userData?.khFloor) floors.push(obj);
      if (!obj.isMesh || !isShown(obj)) return;
      const kind = materialKind(obj.material);
      if (kind === 'skip') return;
      if (!obj.geometry?.attributes?.position) return;
      for (let p = obj; p; p = p.parent) {
        if (p.userData?.khExportAlways) {
          ceilingMeshes.push(obj);
          break;
        }
      }
      if (kind === 'emitter') {
        emitters.push(obj);
        return;
      }
      box.setFromObject(obj).getSize(sizeV);
      const bake = sizeV.length() >= MIN_BAKE_SIZE_M;
      solids.push({ mesh: obj, bake });
    });
    this.bakedMeshes = solids.filter((s) => s.bake).map((s) => s.mesh);
    if (!this.bakedMeshes.length) throw new Error('nothing-to-bake');

    // ۲) UVِ دوم (هندسه‌ی مش‌های پخته‌شونده بی‌ایندکس می‌شود)
    const atlas = buildLightmapAtlas(this.bakedMeshes, size);
    this.atlas = atlas;
    this.usedHeight = Math.max(1, Math.min(size, Math.ceil(atlas.usedHeight)));

    // ۳) درختِ مثلث‌ها (همه‌ی جامدها + LEDها)
    let triTotal = 0;
    const geoms = [];
    [...solids.map((s) => s.mesh), ...emitters].forEach((mesh) => {
      const geo = mesh.geometry;
      const count = geo.index ? geo.index.count : geo.attributes.position.count;
      triTotal += Math.floor(count / 3);
      geoms.push({ mesh, kind: emitters.includes(mesh) ? 'emitter' : 'solid' });
    });
    const positions = new Float32Array(triTotal * 9);
    const matIds = new Int32Array(triTotal);
    let t = 0;
    const v = new THREE.Vector3();
    for (const { mesh, kind } of geoms) {
      const mid = addMaterial(mesh.material, kind);
      const geo = mesh.geometry;
      const pos = geo.attributes.position;
      const idx = geo.index;
      const count = idx ? idx.count : pos.count;
      for (let i = 0; i + 2 < count; i += 3) {
        for (let k = 0; k < 3; k += 1) {
          const vi = idx ? idx.getX(i + k) : i + k;
          v.fromBufferAttribute(pos, vi).applyMatrix4(mesh.matrixWorld);
          positions[t * 9 + k * 3] = v.x;
          positions[t * 9 + k * 3 + 1] = v.y;
          positions[t * 9 + k * 3 + 2] = v.z;
        }
        matIds[t] = mid;
        t += 1;
      }
    }
    const bvh = buildBvh(positions.subarray(0, t * 9), matIds.subarray(0, t));
    this.bvh = bvh;
    this.matTex = makeDataTexture(Float32Array.from(matData), matData.length / 4);

    // ۴) چراغ‌های سقفی — یکی برای هر اتاق، به نسبتِ «روشنایی»ِ همان اتاق
    this.lights = this.buildRoomLights(floors, ceilingMeshes);

    // ۵) نقشه‌ی موقعیت/جهت
    this.posRT = makeRT(size, size, THREE.FloatType);
    this.nrmRT = makeRT(size, size, THREE.HalfFloatType); // جهت دقتِ ۱۶ بیتی کافی است
    this.accRT = makeRT(size, size, this.accType);
    this.tmpRT = makeRT(size, size, this.accType);
    this.resolvedRT = new THREE.WebGLRenderTarget(size, size, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
    });
    this.resolvedRT.texture.channel = 1; // UVِ دوم
    this.resolvedRT.texture.colorSpace = THREE.LinearSRGBColorSpace;
    this.lightMap = this.resolvedRT.texture;

    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    // آیا کارت گرافیک اجازه‌ی نوشتن در این نوع تصویرها را می‌دهد؟
    const gl = renderer.getContext();
    const fb = {};
    [['pos', this.posRT], ['nrm', this.nrmRT], ['acc', this.accRT], ['out', this.resolvedRT]].forEach(([name, rt]) => {
      this.withTarget(rt, () => {
        fb[name] = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
      });
    });
    const badFb = Object.keys(fb).filter((k) => !fb[k]);
    if (badFb.length) throw new Error(`rt-unsupported:${badFb.join(',')}`);

    this.renderGBuffer();
    this.setupPasses();
    this.clearTarget(this.accRT);
    this.clearTarget(this.resolvedRT);
    // ساختِ شیدرِ اصلی همین حالا (یک ردیف)، تا اگر روی این مرورگر ساخته نشد فوراً معلوم شود
    this.traceBand(0, 1);
    this.clearTarget(this.accRT);
    if (this.shaderErrors && this.shaderErrors.length) throw new Error(`shader:${this.shaderErrors[0]}`);

    this.stats = {
      triangles: bvh.triCount,
      bakedMeshes: this.bakedMeshes.length,
      charts: atlas.chartCount,
      texelsPerMeter: atlas.density,
      usedHeight: this.usedHeight,
      lights: this.lights.count,
      precision: this.accType === THREE.FloatType ? 'float32' : 'float16',
      lightsY: this.lights.uInfo.slice(0, this.lights.count).map((v) => +v.x.toFixed(2)),
    };
    this.probe('gbuffer');
    return this.stats;
  }

  buildRoomLights(floors, ceilingMeshes) {
    const rects = [];
    const info = [];
    const L = [];
    const box = new THREE.Box3();
    const raycaster = new THREE.Raycaster();
    const powers = [];
    for (const floor of floors.slice(0, MAX_LIGHTS)) {
      box.setFromObject(floor);
      if (box.isEmpty()) continue;
      const cx = (box.min.x + box.max.x) / 2;
      const cz = (box.min.z + box.max.z) / 2;
      const top = box.max.y;
      let y = top + 2.72;
      if (ceilingMeshes.length) {
        raycaster.set(new THREE.Vector3(cx, top + 0.05, cz), new THREE.Vector3(0, 1, 0));
        const hit = raycaster.intersectObjects(ceilingMeshes, false)[0];
        if (hit) y = hit.point.y - 0.03;
      }
      const hw = ((box.max.x - box.min.x) / 2) * ROOM_LIGHT_SHRINK;
      const hd = ((box.max.z - box.min.z) / 2) * ROOM_LIGHT_SHRINK;
      const area = 4 * hw * hd;
      if (area < 0.01) continue;
      const pct = Number(floor.userData.lightPct);
      const factor = (Number.isFinite(pct) ? pct : 70) / 70;
      const radiance = ROOM_LIGHT_L * factor;
      if (radiance <= 0) continue;
      rects.push(new THREE.Vector4(cx - hw, cz - hd, cx + hw, cz + hd));
      const col = ROOM_LIGHT_COLOR.clone().multiplyScalar(radiance);
      L.push(new THREE.Vector3(col.r, col.g, col.b));
      info.push({ y, area });
      powers.push(radiance * area);
    }
    const total = powers.reduce((a, b) => a + b, 0);
    const uRect = [];
    const uInfo = [];
    const uL = [];
    let cdf = 0;
    for (let i = 0; i < MAX_LIGHTS; i += 1) {
      if (i < rects.length) {
        const p = powers[i] / total;
        cdf += p;
        uRect.push(rects[i]);
        uInfo.push(new THREE.Vector4(info[i].y, i === rects.length - 1 ? 1.01 : cdf, info[i].area, p));
        uL.push(L[i]);
      } else {
        uRect.push(new THREE.Vector4());
        uInfo.push(new THREE.Vector4(0, 2, 0, 1));
        uL.push(new THREE.Vector3());
      }
    }
    return { count: rects.length, uRect, uInfo, uL };
  }

  // -------------------------------------------------------------------------------------------------
  withTarget(rt, fn) {
    const { renderer } = this;
    const prevRT = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevXr = renderer.xr.enabled;
    renderer.xr.enabled = false;
    renderer.autoClear = false;
    // خطای ساختِ شیدر (مثلاً ناسازگاریِ یک مرورگر) ثبت شود تا به کاربر گفته شود، نه این‌که بی‌صدا سیاه بماند
    const prevOnShaderError = renderer.debug.onShaderError;
    renderer.debug.onShaderError = (gl, program, glVS, glFS) => {
      const log = (gl.getShaderInfoLog(glFS) || '') + (gl.getShaderInfoLog(glVS) || '') || gl.getProgramInfoLog(program) || 'link';
      this.shaderErrors = this.shaderErrors || [];
      if (this.shaderErrors.length < 3) this.shaderErrors.push(String(log).trim().slice(0, 160));
      console.error('[LightBaker] shader error', log); // eslint-disable-line no-console
    };
    renderer.setRenderTarget(rt);
    try {
      fn();
    } finally {
      renderer.setRenderTarget(prevRT);
      renderer.autoClear = prevAutoClear;
      renderer.xr.enabled = prevXr;
      renderer.debug.onShaderError = prevOnShaderError;
    }
  }

  clearTarget(rt) {
    const { renderer } = this;
    const prevColor = new THREE.Color();
    renderer.getClearColor(prevColor);
    const prevAlpha = renderer.getClearAlpha();
    this.withTarget(rt, () => {
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, false, false);
    });
    renderer.setClearColor(prevColor, prevAlpha);
  }

  renderGBuffer() {
    const scene = new THREE.Scene();
    const mat = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: GBUFFER_VERT,
      fragmentShader: GBUFFER_FRAG,
      uniforms: { uMode: { value: 0 }, uJitter: { value: new THREE.Vector2() } },
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    });
    const proxies = [];
    for (const mesh of this.bakedMeshes) {
      if (!mesh.geometry.attributes.normal) mesh.geometry.computeVertexNormals();
      const m = new THREE.Mesh(mesh.geometry, mat);
      m.matrixAutoUpdate = false;
      m.matrix.copy(mesh.matrixWorld);
      m.matrixWorld.copy(mesh.matrixWorld);
      m.frustumCulled = false;
      scene.add(m);
      proxies.push(m);
    }
    scene.matrixWorldAutoUpdate = false;
    const texel = 2 / this.size;
    // اول با جابه‌جاییِ نیم‌نقطه‌ای (تا نقطه‌های لبه‌ی مثلث‌های باریک هم پر شوند)، آخر بدونِ جابه‌جایی (دقیق)
    const jitters = [
      [0.45, 0.45],
      [-0.45, 0.45],
      [0.45, -0.45],
      [-0.45, -0.45],
      [0, 0],
    ];
    this.clearTarget(this.posRT);
    this.clearTarget(this.nrmRT);
    [0, 1].forEach((mode) => {
      mat.uniforms.uMode.value = mode;
      this.withTarget(mode === 0 ? this.posRT : this.nrmRT, () => {
        for (const [jx, jy] of jitters) {
          mat.uniforms.uJitter.value.set(jx * texel, jy * texel);
          mat.uniformsNeedUpdate = true;
          this.renderer.render(scene, this.camera);
        }
      });
    });
    mat.dispose();
    proxies.forEach((p) => scene.remove(p));
  }

  setupPasses() {
    const defines = { DATA_W: DATA_TEX_WIDTH, MAX_LIGHTS };
    const quadGeo = new THREE.PlaneGeometry(2, 2);
    const make = (fragmentShader, uniforms) => {
      const material = new THREE.RawShaderMaterial({
        glslVersion: THREE.GLSL3,
        vertexShader: FULLSCREEN_VERT,
        fragmentShader,
        uniforms,
        defines,
        depthTest: false,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(quadGeo, material);
      mesh.frustumCulled = false;
      const scene = new THREE.Scene();
      scene.add(mesh);
      return { scene, material };
    };
    const L = this.lights;
    this.tracePass = make(TRACE_FRAG, {
      uPrev: { value: this.accRT.texture },
      uPos: { value: this.posRT.texture },
      uNrm: { value: this.nrmRT.texture },
      uNodes: { value: this.bvh.nodeTex },
      uTris: { value: this.bvh.triTex },
      uMats: { value: this.matTex },
      uFrame: { value: 0 },
      uBounces: { value: this.bounces },
      uClamp: { value: this.clamp },
      uSunDir: { value: this.sun.dir },
      uSunE: { value: new THREE.Vector3(this.sun.color.r, this.sun.color.g, this.sun.color.b).multiplyScalar(this.sun.E) },
      uSunCos: { value: Math.cos(THREE.MathUtils.degToRad(0.8)) },
      uSkyZenith: { value: new THREE.Vector3(0.32, 0.48, 0.8).multiplyScalar(0.9) },
      uSkyHorizon: { value: new THREE.Vector3(0.78, 0.84, 0.92).multiplyScalar(1.1) },
      uGround: { value: new THREE.Vector3(0.3, 0.28, 0.25).multiplyScalar(0.7) },
      uLightCount: { value: L.count },
      uLightRect: { value: L.uRect },
      uLightInfo: { value: L.uInfo },
      uLightL: { value: L.uL },
    });
    this.copyPass = make(COPY_FRAG, { uSrc: { value: this.tmpRT.texture } });
    this.denoisePass = make(DENOISE_FRAG, {
      uSrc: { value: null },
      uPos: { value: this.posRT.texture },
      uNrm: { value: this.nrmRT.texture },
      uStep: { value: 1 },
      uTexelWorld: { value: 1 / Math.max(1e-3, this.atlas.density) },
      uSigmaL: { value: 0.6 },
    });
    this.resolvePass = make(RESOLVE_FRAG, { uSrc: { value: this.accRT.texture }, uPos: { value: this.posRT.texture } });
    this.quadGeo = quadGeo;
  }

  renderPass(pass, target, scissor) {
    if (scissor) {
      target.scissor.set(scissor[0], scissor[1], scissor[2], scissor[3]);
      target.scissorTest = true;
    } else {
      target.scissorTest = false;
    }
    this.withTarget(target, () => this.renderer.render(pass.scene, this.camera));
    target.scissorTest = false;
  }

  // یک نوار: ردیابی در تصویرِ موقت، بعد کپیِ همان نوار به تصویرِ اصلی
  traceBand(y0, rows) {
    const band = [0, y0, this.size, rows];
    this.tracePass.material.uniforms.uFrame.value = this.frame;
    this.frame += 1;
    this.renderPass(this.tracePass, this.tmpRT, band);
    this.renderPass(this.copyPass, this.accRT, band);
  }

  // کارت گرافیک دستورها را با تأخیر اجرا می‌کند؛ فاصله‌ی فریم‌ها زمانِ واقعیِ کار را نشان نمی‌دهد (روی آیفون
  // تکه‌ها بی‌صدا آن‌قدر بزرگ شدند که سیستم‌عامل اجرایشان را قطع کرد و چیزی نوشته نشد). با خواندنِ یک نقطه
  // صبر می‌کنیم تا کارِ واقعاً تمام شود و زمانِ دقیقِ هر تکه را اندازه می‌گیریم.
  sync() {
    if (!this.syncBuf) this.syncBuf = this.accType === THREE.FloatType ? new Float32Array(4) : new Uint16Array(4);
    this.renderer.readRenderTargetPixels(this.accRT, 0, 0, 1, 1, this.syncBuf);
  }

  // هر فریم صدا زده می‌شود. چند تکه (نوار) تا وقتی سهمِ این فریم (حدودِ ۲۲ میلی‌ثانیه) تمام شود؛ اندازه‌ی نوار
  // طوری تنظیم می‌شود که هر تکه حدودِ ۱۰ میلی‌ثانیه طول بکشد (خیلی دور از مرزِ قطع‌شدن توسطِ سیستم‌عامل)
  step() {
    if (this.finished || !this.tracePass) return false;
    const TARGET_PASS_MS = 10;
    const FRAME_BUDGET_MS = 22;
    const maxRows = Math.max(1, Math.min(this.usedHeight, Math.floor((2048 * 1024) / this.size))); // سقفِ ایمنی؛ اندازه‌ی واقعی را زمان‌سنجی تعیین می‌کند
    if (!this.rows) this.rows = 2;
    const t0 = performance.now();
    let passes = 0;
    let sweepDone = false;
    while (passes < 24) {
      const rows = Math.max(1, Math.min(this.usedHeight - this.cursor, this.rows));
      const ps = performance.now();
      this.traceBand(this.cursor, rows);
      this.sync();
      const ms = Math.max(0.05, performance.now() - ps);
      const ideal = (rows * TARGET_PASS_MS) / ms;
      this.rows = Math.max(1, Math.min(maxRows, Math.round(this.rows * 0.5 + Math.min(ideal, this.rows * 2) * 0.5)));
      this.lastPassMs = ms;
      this.cursor += rows;
      passes += 1;
      if (this.cursor >= this.usedHeight) {
        this.cursor = 0;
        this.samples += 1;
        sweepDone = true;
        if (this.samples === 1 || this.samples === 16) this.probe(`s${this.samples}`);
        if (this.samples >= this.targetSamples) break;
      }
      if (performance.now() - t0 > FRAME_BUDGET_MS) break;
    }
    this.stats.rowsPerPass = this.rows;
    this.stats.passMs = +(this.lastPassMs || 0).toFixed(1);
    // نمایش: بعد از هر دورِ کامل؛ در دورِ اول هم هر چند فریم (تا کاربر از همان اول تصویر را ببیند)
    this.framesSinceResolve = (this.framesSinceResolve || 0) + 1;
    if (sweepDone || (this.samples < 1 && this.framesSinceResolve >= 12)) {
      this.resolve();
      this.framesSinceResolve = 0;
    }
    return sweepDone;
  }

  // عیب‌یابی: یک ردیف از وسطِ نقشه خوانده می‌شود — چند درصد پوشش دارد و میانگینِ نور چقدر است
  probe(tag) {
    try {
      const { renderer, size } = this;
      const y = Math.floor(this.usedHeight / 2);
      const pos = new Float32Array(size * 4);
      renderer.readRenderTargetPixels(this.posRT, 0, y, size, 1, pos);
      let covered = 0;
      for (let i = 0; i < size; i += 1) if (pos[i * 4 + 3] > 0.5) covered += 1;
      const out = { coverage: +((covered / size) * 100).toFixed(1) };
      if (tag !== 'gbuffer') {
        const acc = this.accType === THREE.FloatType ? new Float32Array(size * 4) : new Uint16Array(size * 4);
        renderer.readRenderTargetPixels(this.accRT, 0, y, size, 1, acc);
        const val = (i) => (this.accType === THREE.FloatType ? acc[i] : THREE.DataUtils.fromHalfFloat(acc[i]));
        let sum = 0;
        let n = 0;
        let cnt = 0;
        for (let i = 0; i < size; i += 1) {
          if (pos[i * 4 + 3] > 0.5) {
            sum += val(i * 4 + 1);
            cnt = Math.max(cnt, val(i * 4 + 3));
            n += 1;
          }
        }
        out.meanLight = n ? +(sum / n).toFixed(3) : 0;
        out.samplesSeen = cnt;
      }
      this.stats[`probe_${tag}`] = out;
    } catch (e) {
      this.stats[`probe_${tag}`] = { error: String(e?.message || e).slice(0, 60) };
    }
  }

  get progress() {
    return Math.min(1, (this.samples + this.cursor / this.usedHeight) / this.targetSamples);
  }

  resolve(src = this.accRT) {
    this.resolvePass.material.uniforms.uSrc.value = src.texture;
    this.renderPass(this.resolvePass, this.resolvedRT, null);
  }

  // حذفِ نویز + نسخه‌ی نهایی؛ بعد حافظه‌ی موقت آزاد می‌شود
  finish() {
    if (this.finished) return;
    const uniforms = this.denoisePass.material.uniforms;
    // دو تصویرِ کاری به نوبت (حافظه‌ی اضافه لازم نیست)
    let src = this.accRT;
    let dst = this.tmpRT;
    [1, 2, 4].forEach((stepSize, i) => {
      uniforms.uSrc.value = src.texture;
      uniforms.uStep.value = stepSize;
      uniforms.uSigmaL.value = i === 0 ? 0.8 : 0.5;
      this.renderPass(this.denoisePass, dst, null);
      const t = src;
      src = dst;
      dst = t;
    });
    this.resolve(src);
    this.finished = true;
    this.releaseWork();
  }

  releaseWork() {
    [this.posRT, this.nrmRT, this.accRT, this.tmpRT].forEach((rt) => rt && rt.dispose());
    this.posRT = null;
    this.nrmRT = null;
    this.accRT = null;
    this.tmpRT = null;
    if (this.bvh) {
      this.bvh.nodeTex.dispose();
      this.bvh.triTex.dispose();
    }
    if (this.matTex) this.matTex.dispose();
    [this.tracePass, this.copyPass, this.denoisePass, this.resolvePass].forEach((p) => p && p.material.dispose());
    if (this.quadGeo) this.quadGeo.dispose();
    this.tracePass = null;
  }

  dispose() {
    this.releaseWork();
    if (this.resolvedRT) this.resolvedRT.dispose();
    this.resolvedRT = null;
  }
}
